import { Set } from 'immutable';

import { EBAY, ETSY, FAIRE, SHOPIFY } from '@/constants/channels';
import { PROFILE_TYPE } from '@/constants/profiles';
import { VELA } from '.';

export const PAGE_SIZE = 25;

export const COPY_LIMIT = 250;

export const PUBLISH_LIMIT = 100;

export const MAX_FILTER_ITEMS = 5;

export const ORDER = {
  ASCENDING: true,
  DESCENDING: false,
};

export const ORDER_INDICATOR = {
  [ORDER.ASCENDING]: ' ↑',
  [ORDER.DESCENDING]: ' ↓',
};

export const SELECTION = {
  ALL: 'all',
  COMPLETE: 'complete',
  INCOMPLETE: 'incomplete',
  NONE: 'none',
  PAGE: 'page',
};

export const GRADES = ['F', 'D', 'C', 'B', 'A'];

export const STATUS = {
  [EBAY]: {
    ACTIVE: 'active',
  },
  [ETSY]: {
    ACTIVE: 'active',
    DRAFT: 'draft',
    INACTIVE: 'inactive',
  },
  [FAIRE]: {
    PUBLISHED: 'faire_published',
    DRAFT: 'faire_draft',
    UNPUBLISHED: 'faire_unpublished',
  },
  [SHOPIFY]: {
    ACTIVE: 'active',
    ARCHIVED: 'archived',
    DRAFT: 'draft',
  },
  [VELA]: {
    COPIED: 'copied',
    IMPORTED: 'imported',
    SCHEDULED: 'scheduled',
    TEMPLATE: 'template',
    UNPUBLISHED: 'unpublished',
    STAGING: 'staging',
  },
};

export const STATUS_NAME = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
  COPY: 'Copy',
  DRAFT: 'Draft',
  IMPORTED: 'Imported',
  INACTIVE: 'Inactive',
  PUBLISHED: 'Published',
  UNPUBLISHED: 'Unpublished',
  STAGING: 'Staging',
};

export const CHANNEL_STATUSES = {
  [EBAY]: [
    STATUS[EBAY].ACTIVE,
  ],
  [ETSY]: [
    STATUS[ETSY].ACTIVE,
    STATUS[ETSY].DRAFT,
    STATUS[ETSY].INACTIVE,
  ],
  [FAIRE]: [
    STATUS[FAIRE].PUBLISHED,
    STATUS[FAIRE].DRAFT,
    STATUS[FAIRE].UNPUBLISHED,
  ],
  [SHOPIFY]: [
    STATUS[SHOPIFY].ACTIVE,
    STATUS[SHOPIFY].DRAFT,
    STATUS[SHOPIFY].ARCHIVED,
  ],
};

export const VELA_STATUSES = [
  STATUS[VELA].COPIED,
  STATUS[VELA].UNPUBLISHED,
  STATUS[VELA].STAGING,
];

export const STATUS_TYPE = {
  CHANNEL: 'channel',
  VELA,
};

export const PUBLISH_ACTION = {
  PUBLISH: 'Publish',
  SAVE_AS_DRAFT: 'Save as draft',
  SCHEDULE: 'Schedule',
  RESCHEDULE: 'Reschedule',
};

export const FILTER = {
  CATEGORY: 'category',
  COLLECTIONS: 'collections',
  EXISTING: 'existing',
  IS_COMPLETE: 'isComplete',
  ITEM_CATEGORY: 'itemCategory',
  MATERIALS: 'materials',
  PAYMENT_POLICY: 'paymentPolicy',
  PRODUCT_TYPE: 'productType',
  PROFILE: 'profile',
  PROFILE_TYPE: 'profileType',
  RETURN_POLICY: 'returnPolicy',
  SCORE: 'score',
  SECTION: 'sections',
  SHIPPING_POLICY: 'shippingPolicy',
  STATUS: 'status',
  STORE_CATEGORY: 'storeCategory',
  TAGS: 'tags',
  TAXONOMY: 'taxonomy',
  TITLE: 'title',
  VENDOR: 'vendor',
};

export const FILTER_NAME = {
  [FILTER.CATEGORY]: 'Category',
  [FILTER.COLLECTIONS]: 'Collections',
  [FILTER.IS_COMPLETE]: 'Status',
  [FILTER.ITEM_CATEGORY]: 'Item category',
  [FILTER.MATERIALS]: 'Materials',
  [FILTER.PAYMENT_POLICY]: 'Payment policy',
  [FILTER.PRODUCT_TYPE]: 'Product type',
  [FILTER.PROFILE]: 'Profile',
  [FILTER.RETURN_POLICY]: 'Return policy',
  [FILTER.SCORE]: 'Listing score',
  [FILTER.SECTION]: 'Section',
  [FILTER.SHIPPING_POLICY]: 'Shipping policy',
  [FILTER.STORE_CATEGORY]: 'Store category',
  [FILTER.TAGS]: 'Tags',
  [FILTER.TAXONOMY]: 'Category',
  [FILTER.VENDOR]: 'Vendor',
};

export const FILTER_API_KEY = {
  [FILTER.CATEGORY]: 'category_ids',
  [FILTER.COLLECTIONS]: 'collection_ids',
  [FILTER.EXISTING]: 'existing',
  [FILTER.IS_COMPLETE]: 'is_complete',
  [FILTER.ITEM_CATEGORY]: 'category_ids',
  [FILTER.MATERIALS]: 'materials',
  [FILTER.PAYMENT_POLICY]: 'payment_policy_ids',
  [FILTER.PRODUCT_TYPE]: 'product_types',
  [FILTER.PROFILE]: {
    [PROFILE_TYPE.AI]: 'ai_profile_ids',
    [PROFILE_TYPE.STANDARD]: 'profile_ids',
  },
  [FILTER.RETURN_POLICY]: 'return_policy_ids',
  [FILTER.SCORE]: 'scores',
  [FILTER.SECTION]: 'section_ids',
  [FILTER.SHIPPING_POLICY]: 'shipping_policy_ids',
  [FILTER.STATUS]: 'status',
  [FILTER.STORE_CATEGORY]: 'custom_category_ids',
  [FILTER.TAGS]: 'tags',
  [FILTER.TAXONOMY]: 'taxonomy_ids',
  [FILTER.TITLE]: 'title',
  [FILTER.VENDOR]: 'vendors',
};

export const CHANNEL_FILTERS = {
  [EBAY]: [
    FILTER.EXISTING,
    FILTER.IS_COMPLETE,
    FILTER.SCORE,
    FILTER.PROFILE,
    FILTER.ITEM_CATEGORY,
    FILTER.STORE_CATEGORY,
    FILTER.SHIPPING_POLICY,
  ],
  [ETSY]: [
    FILTER.EXISTING,
    FILTER.IS_COMPLETE,
    FILTER.SCORE,
    FILTER.PROFILE,
    FILTER.TAXONOMY,
    FILTER.SECTION,
    FILTER.TAGS,
    FILTER.MATERIALS,
  ],
  [FAIRE]: [
    FILTER.EXISTING,
    FILTER.IS_COMPLETE,
    FILTER.SCORE,
    FILTER.PROFILE,
    FILTER.CATEGORY,
  ],
  [SHOPIFY]: [
    FILTER.EXISTING,
    FILTER.IS_COMPLETE,
    FILTER.SCORE,
    FILTER.PROFILE,
    FILTER.CATEGORY,
    FILTER.COLLECTIONS,
    FILTER.PRODUCT_TYPE,
    FILTER.VENDOR,
    FILTER.TAGS,
  ],
};

export const LISTING = {
  [EBAY]: {
    CHANNEL_PRODUCT_ID: 'channel_listing_id',
    CREATED_AT: 'created_at',
    IS_COMPLETE: 'is_complete',
    LINK_ID: 'link_id',
    LINKED_PRODUCTS_SHOP_IDS: 'merged_products_shop_ids',
    PHOTO: 'photo',
    PRICE: 'price',
    PRICE_VARIANT: 'price_variant',
    PRODUCT_ID: 'id',
    PUBLISHED_AT: 'published_at',
    SCHEDULED_AT: 'scheduled_at',
    QUANTITY: 'quantity',
    TITLE: 'title',
    OVERALL_SCORE: 'overall_score',
    SHOPS: 'links_count',
  },
  [ETSY]: {
    CHANNEL_PRODUCT_ID: 'listing_id',
    CREATED_AT: 'creation_tsz',
    EXPIRES_AT: 'ending_tsz',
    IS_COMPLETE: 'is_complete',
    LINK_ID: 'link_id',
    LINKED_PRODUCTS_SHOP_IDS: 'merged_products_shop_ids',
    PHOTO: 'photo',
    PRICE: 'price',
    PRICE_VARIANT: 'price_variant',
    PRODUCT_ID: 'id',
    QUANTITY: 'quantity',
    SCHEDULED_AT: 'scheduled_at',
    SECTION_ID: 'section_id',
    TITLE: 'title',
    UPDATED_AT: '_hive_last_modified_tsz',
    OVERALL_SCORE: 'overall_score',
    SHOPS: 'links_count',
  },
  [FAIRE]: {
    CHANNEL_PRODUCT_ID: 'channel_listing_id',
    CREATED_AT: 'created_at',
    IS_COMPLETE: 'is_complete',
    LINK_ID: 'link_id',
    LINKED_PRODUCTS_SHOP_IDS: 'merged_products_shop_ids',
    PHOTO: 'photo',
    PRICE: 'price',
    PRICE_VARIANT: 'price_variant',
    PRODUCT_ID: 'id',
    PUBLISHED_AT: 'published_at',
    QUANTITY: 'available_quantity',
    SCHEDULED_AT: 'scheduled_at',
    TITLE: 'title',
    OVERALL_SCORE: 'overall_score',
    SHOPS: 'links_count',
  },
  [SHOPIFY]: {
    CHANNEL_PRODUCT_ID: 'product_id',
    COLLECTION_IDS: 'collection_ids',
    IS_COMPLETE: 'is_complete',
    LINK_ID: 'link_id',
    LINKED_PRODUCTS_SHOP_IDS: 'merged_products_shop_ids',
    PHOTO: 'photo',
    PRICE: 'price',
    PRICE_VARIANT: 'price_variant',
    PRODUCT_ID: 'id',
    PUBLISHED_AT: 'published_at',
    SCHEDULED_AT: 'scheduled_at',
    QUANTITY: 'quantity',
    TITLE: 'title',
    UPDATED_AT: 'updated_at',
    OVERALL_SCORE: 'overall_score',
    SHOPS: 'links_count',
  },
  [VELA]: {
    CHANNEL_PRODUCT_ID: 'channelProductId',
    COLLECTIONS: 'collections',
    CREATED_AT: 'createdAt',
    EXPIRES_AT: 'expiresAt',
    IS_COMPLETE: 'isComplete',
    LINK_ID: 'linkId',
    LINKED_PRODUCTS_SHOP_IDS: 'linkedProductsShopIds',
    LINKED_PRODUCTS_SHOP_CHANNELS: 'linkedProductsShopChannels',
    PHOTO: 'photo',
    PRICE: 'price',
    PRICE_VARIANT: 'priceVariant',
    PRODUCT_ID: 'productId',
    PUBLISHED_AT: 'publishedAt',
    SCHEDULED_AT: 'scheduledAt',
    QUANTITY: 'quantity',
    SCORE: 'score',
    SECTION: 'section',
    TITLE: 'title',
    UPDATED_AT: 'updatedAt',
  },
};

export const ALPHABETICAL_ORDER = {
  [EBAY]: { by: LISTING[EBAY].TITLE, type: true },
  [ETSY]: { by: LISTING[ETSY].TITLE, type: true },
  [FAIRE]: { by: LISTING[FAIRE].TITLE, type: true },
  [SHOPIFY]: { by: LISTING[SHOPIFY].TITLE, type: true },
};

export const TABLE_COLUMN = {
  COLLECTIONS: 'collections',
  CONTROLS: 'controls',
  CREATED: 'created',
  EXPIRED: 'expired',
  EXPIRES: 'expires',
  PRICE: 'price',
  PUBLISHED: 'published',
  QUANTITY: 'quantity',
  SECTION: 'section',
  SCORE: 'score',
  SHOPS: 'shops',
  TITLE: 'title',
  TOGGLE: 'toggle',
  UPDATED: 'updated',
};

export const TABLE = {
  COLUMN: TABLE_COLUMN,
  COLUMNS: {
    [EBAY]: {
      [STATUS[EBAY].ACTIVE]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.CREATED,
      ],
      [STATUS[VELA].COPIED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
      [STATUS[VELA].UNPUBLISHED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
      [STATUS[VELA].STAGING]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
    },
    [ETSY]: {
      [STATUS[ETSY].ACTIVE]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.EXPIRES,
        TABLE_COLUMN.SECTION,
      ],
      [STATUS[ETSY].DRAFT]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.UPDATED,
        TABLE_COLUMN.SECTION,
      ],
      [STATUS[ETSY].INACTIVE]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.EXPIRED,
        TABLE_COLUMN.SECTION,
      ],
      [STATUS[VELA].COPIED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.SECTION,
      ],
      [STATUS[VELA].UNPUBLISHED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.SECTION,
      ],
      [STATUS[VELA].STAGING]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.SECTION,
      ],
    },
    [FAIRE]: {
      [STATUS[FAIRE].PUBLISHED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.CREATED,
      ],
      [STATUS[FAIRE].DRAFT]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
      [STATUS[FAIRE].UNPUBLISHED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
      [STATUS[VELA].COPIED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
      [STATUS[VELA].UNPUBLISHED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
      [STATUS[VELA].STAGING]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
      ],
    },
    [SHOPIFY]: {
      [STATUS[SHOPIFY].ACTIVE]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.PUBLISHED,
        TABLE_COLUMN.COLLECTIONS,
      ],
      [STATUS[SHOPIFY].DRAFT]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.UPDATED,
        TABLE_COLUMN.COLLECTIONS,
      ],
      [STATUS[SHOPIFY].ARCHIVED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.COLLECTIONS,
      ],
      [STATUS[VELA].COPIED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.COLLECTIONS,
      ],
      [STATUS[VELA].UNPUBLISHED]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.COLLECTIONS,
      ],
      [STATUS[VELA].STAGING]: [
        TABLE_COLUMN.TITLE,
        TABLE_COLUMN.QUANTITY,
        TABLE_COLUMN.PRICE,
        TABLE_COLUMN.COLLECTIONS,
      ],
    },
  },
  COLUMN_NAME: {
    [EBAY]: {
      [TABLE_COLUMN.PRICE]: 'Price',
      [TABLE_COLUMN.CREATED]: 'Created on',
      [TABLE_COLUMN.QUANTITY]: 'Stock',
      [TABLE_COLUMN.SCORE]: 'Score',
      [TABLE_COLUMN.SHOPS]: 'Shops',
      [TABLE_COLUMN.TITLE]: 'Title',
    },
    [ETSY]: {
      [TABLE_COLUMN.EXPIRED]: 'Expired on',
      [TABLE_COLUMN.EXPIRES]: 'Expires on',
      [TABLE_COLUMN.PRICE]: 'Price',
      [TABLE_COLUMN.QUANTITY]: 'Stock',
      [TABLE_COLUMN.SCORE]: 'Score',
      [TABLE_COLUMN.SECTION]: 'Section',
      [TABLE_COLUMN.SHOPS]: 'Shops',
      [TABLE_COLUMN.TITLE]: 'Title',
      [TABLE_COLUMN.UPDATED]: 'Last update',
    },
    [FAIRE]: {
      [TABLE_COLUMN.PRICE]: 'Price',
      [TABLE_COLUMN.CREATED]: 'Created on',
      [TABLE_COLUMN.QUANTITY]: 'Stock',
      [TABLE_COLUMN.SCORE]: 'Score',
      [TABLE_COLUMN.SHOPS]: 'Shops',
      [TABLE_COLUMN.TITLE]: 'Title',
    },
    [SHOPIFY]: {
      [TABLE_COLUMN.COLLECTIONS]: 'Collections',
      [TABLE_COLUMN.PRICE]: 'Price',
      [TABLE_COLUMN.PUBLISHED]: 'Published on',
      [TABLE_COLUMN.QUANTITY]: 'Stock',
      [TABLE_COLUMN.SCORE]: 'Score',
      [TABLE_COLUMN.SHOPS]: 'Shops',
      [TABLE_COLUMN.TITLE]: 'Title',
      [TABLE_COLUMN.UPDATED]: 'Last update',
    },
  },
  COLUMN_ORDER: {
    [EBAY]: {
      [TABLE_COLUMN.PRICE]: LISTING[EBAY].PRICE,
      [TABLE_COLUMN.CREATED]: LISTING[EBAY].CREATED_AT,
      [TABLE_COLUMN.QUANTITY]: LISTING[EBAY].QUANTITY,
      [TABLE_COLUMN.SCORE]: LISTING[EBAY].OVERALL_SCORE,
      [TABLE_COLUMN.SHOPS]: LISTING[EBAY].SHOPS,
      [TABLE_COLUMN.TITLE]: LISTING[EBAY].TITLE,
    },
    [ETSY]: {
      [TABLE_COLUMN.EXPIRED]: LISTING[ETSY].EXPIRES_AT,
      [TABLE_COLUMN.EXPIRES]: LISTING[ETSY].EXPIRES_AT,
      [TABLE_COLUMN.PRICE]: LISTING[ETSY].PRICE,
      [TABLE_COLUMN.QUANTITY]: LISTING[ETSY].QUANTITY,
      [TABLE_COLUMN.SCORE]: LISTING[ETSY].OVERALL_SCORE,
      [TABLE_COLUMN.SECTION]: LISTING[ETSY].SECTION_ID,
      [TABLE_COLUMN.SHOPS]: LISTING[ETSY].SHOPS,
      [TABLE_COLUMN.TITLE]: LISTING[ETSY].TITLE,
      [TABLE_COLUMN.UPDATED]: LISTING[ETSY].UPDATED_AT,
    },
    [FAIRE]: {
      [TABLE_COLUMN.PRICE]: LISTING[FAIRE].PRICE,
      [TABLE_COLUMN.CREATED]: LISTING[FAIRE].CREATED_AT,
      [TABLE_COLUMN.QUANTITY]: LISTING[FAIRE].QUANTITY,
      [TABLE_COLUMN.SCORE]: LISTING[FAIRE].OVERALL_SCORE,
      [TABLE_COLUMN.SHOPS]: LISTING[FAIRE].SHOPS,
      [TABLE_COLUMN.TITLE]: LISTING[FAIRE].TITLE,
    },
    [SHOPIFY]: {
      [TABLE_COLUMN.COLLECTIONS]: LISTING[SHOPIFY].COLLECTION_IDS,
      [TABLE_COLUMN.PRICE]: LISTING[SHOPIFY].PRICE,
      [TABLE_COLUMN.PUBLISHED]: LISTING[SHOPIFY].PUBLISHED_AT,
      [TABLE_COLUMN.QUANTITY]: LISTING[SHOPIFY].QUANTITY,
      [TABLE_COLUMN.SCORE]: LISTING[SHOPIFY].OVERALL_SCORE,
      [TABLE_COLUMN.SHOPS]: LISTING[SHOPIFY].SHOPS,
      [TABLE_COLUMN.TITLE]: LISTING[SHOPIFY].TITLE,
      [TABLE_COLUMN.UPDATED]: LISTING[SHOPIFY].UPDATED_AT,
    },
  },
};

export const STATUS_ORDER = {
  [EBAY]: {
    [STATUS[EBAY].ACTIVE]: Set([
      LISTING[EBAY].PRICE,
      LISTING[EBAY].CREATED_AT,
      LISTING[EBAY].QUANTITY,
      LISTING[EBAY].TITLE,
      LISTING[EBAY].OVERALL_SCORE,
      LISTING[EBAY].SHOPS,
    ]),
    [STATUS[VELA].COPIED]: Set([
      LISTING[EBAY].PRICE,
      LISTING[EBAY].QUANTITY,
      LISTING[EBAY].TITLE,
      LISTING[EBAY].OVERALL_SCORE,
      LISTING[EBAY].SHOPS,
    ]),
    [STATUS[VELA].STAGING]: Set([
      LISTING[EBAY].PRICE,
      LISTING[EBAY].QUANTITY,
      LISTING[EBAY].TITLE,
      LISTING[EBAY].OVERALL_SCORE,
      LISTING[EBAY].SHOPS,
    ]),
    [STATUS[VELA].IMPORTED]: Set([
      LISTING[EBAY].PRICE,
      LISTING[EBAY].QUANTITY,
      LISTING[EBAY].TITLE,
      LISTING[EBAY].OVERALL_SCORE,
      LISTING[EBAY].SHOPS,
    ]),
    [STATUS[VELA].UNPUBLISHED]: Set([
      LISTING[EBAY].PRICE,
      LISTING[EBAY].QUANTITY,
      LISTING[EBAY].TITLE,
      LISTING[EBAY].OVERALL_SCORE,
      LISTING[EBAY].SHOPS,
    ]),
  },
  [ETSY]: {
    [STATUS[ETSY].ACTIVE]: Set([
      LISTING[ETSY].EXPIRES_AT,
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
    [STATUS[ETSY].DRAFT]: Set([
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].UPDATED_AT,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
    [STATUS[ETSY].INACTIVE]: Set([
      LISTING[ETSY].EXPIRES_AT,
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
    [STATUS[VELA].COPIED]: Set([
      LISTING[ETSY].CREATED_AT,
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
    [STATUS[VELA].STAGING]: Set([
      LISTING[ETSY].CREATED_AT,
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
    [STATUS[VELA].IMPORTED]: Set([
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
    [STATUS[VELA].UNPUBLISHED]: Set([
      LISTING[ETSY].PRICE,
      LISTING[ETSY].QUANTITY,
      LISTING[ETSY].SECTION_ID,
      LISTING[ETSY].TITLE,
      LISTING[ETSY].OVERALL_SCORE,
      LISTING[ETSY].SHOPS,
    ]),
  },
  [FAIRE]: {
    [STATUS[FAIRE].PUBLISHED]: Set([
      LISTING[FAIRE].PRICE,
      LISTING[FAIRE].CREATED_AT,
      LISTING[FAIRE].QUANTITY,
      LISTING[FAIRE].TITLE,
      LISTING[FAIRE].OVERALL_SCORE,
      LISTING[FAIRE].SHOPS,
    ]),
    [STATUS[FAIRE].DRAFT]: Set([
      LISTING[FAIRE].PRICE,
      LISTING[FAIRE].QUANTITY,
      LISTING[FAIRE].TITLE,
      LISTING[FAIRE].OVERALL_SCORE,
      LISTING[FAIRE].SHOPS,
    ]),
    [STATUS[FAIRE].UNPUBLISHED]: Set([
      LISTING[FAIRE].PRICE,
      LISTING[FAIRE].QUANTITY,
      LISTING[FAIRE].TITLE,
      LISTING[FAIRE].OVERALL_SCORE,
      LISTING[FAIRE].SHOPS,
    ]),
    [STATUS[VELA].COPIED]: Set([
      LISTING[FAIRE].PRICE,
      LISTING[FAIRE].QUANTITY,
      LISTING[FAIRE].TITLE,
      LISTING[FAIRE].OVERALL_SCORE,
      LISTING[FAIRE].SHOPS,
    ]),
    [STATUS[VELA].STAGING]: Set([
      LISTING[FAIRE].PRICE,
      LISTING[FAIRE].QUANTITY,
      LISTING[FAIRE].TITLE,
      LISTING[FAIRE].OVERALL_SCORE,
      LISTING[FAIRE].SHOPS,
    ]),
    [STATUS[VELA].UNPUBLISHED]: Set([
      LISTING[FAIRE].PRICE,
      LISTING[FAIRE].QUANTITY,
      LISTING[FAIRE].TITLE,
      LISTING[FAIRE].OVERALL_SCORE,
      LISTING[FAIRE].SHOPS,
    ]),
  },
  [SHOPIFY]: {
    [STATUS[SHOPIFY].ACTIVE]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].PUBLISHED_AT,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
    [STATUS[SHOPIFY].DRAFT]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].UPDATED_AT,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
    [STATUS[SHOPIFY].ARCHIVED]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
    [STATUS[VELA].COPIED]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
    [STATUS[VELA].STAGING]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
    [STATUS[VELA].IMPORTED]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
    [STATUS[VELA].UNPUBLISHED]: Set([
      LISTING[SHOPIFY].COLLECTION_IDS,
      LISTING[SHOPIFY].PRICE,
      LISTING[SHOPIFY].QUANTITY,
      LISTING[SHOPIFY].TITLE,
      LISTING[SHOPIFY].OVERALL_SCORE,
      LISTING[SHOPIFY].SHOPS,
    ]),
  },
};
